import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/useT';
import { useBotState } from '@/bot/hooks';
import { isStopWinHalt } from '@/lib/contracts';
import type { BotState } from '@/lib/api';

/**
 * Stop-Win celebration.
 *
 * The approved dashboard congratulated the customer the moment the bot halted
 * on the daily Stop-Win target (public/app.js maybeCelebrateStopWin, markup at
 * public/index.html:705-883, styles at public/style.css:2377-2828). 4B-2 rebuilt
 * the dashboard in React and this overlay was never carried across, so hitting
 * the target stopped congratulating anyone — which is what Adlain reported.
 *
 * Everything here is ported rather than re-invented: the trigger condition, the
 * once-per-day localStorage flag (same key prefix, so a customer who already
 * celebrated today on the old build is not congratulated twice), the 6-second
 * auto-close that pauses on hover, the money count-up easing, and the confetti
 * physics and palette.
 *
 * The trigger reads the SAME query key the bot screens already poll, so it adds
 * no request; it just needs to be mounted somewhere that stays mounted while
 * the customer is on a bot screen.
 *
 * Preview without waiting for a real halt: open any bot screen with
 * #celebrate-test in the URL (kept from the approved build).
 */

const FLAG_PREFIX = 'smallx_celebrated_';
const AUTOCLOSE_SECONDS = 6;

const money = (n: number): string =>
  `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;

type Snapshot = {
  netProfitUsd: number;
  cycles: number;
  wins: number;
  losses: number;
};

const PREVIEW: Snapshot = { netProfitUsd: 7.5, cycles: 4, wins: 3, losses: 1 };

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false; // private mode: celebrate, just possibly twice
  }
}
function writeFlag(key: string): void {
  try {
    localStorage.setItem(key, '1');
  } catch {
    /* private mode */
  }
}

/**
 * Keep at most MAX_FLAGS celebration keys.
 *
 * The old key was one per day, so it could never accumulate. Keying on the halt
 * means a busy customer writes several a day and they would otherwise grow
 * without limit. Sorting by the leading dayKey keeps the most recent, and the
 * key just written is never dropped.
 */
const MAX_FLAGS = 40;
function pruneFlags(keep: string): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(FLAG_PREFIX)) keys.push(k);
    }
    if (keys.length <= MAX_FLAGS) return;
    keys.sort(); // dayKey leads the key, so this is chronological
    for (const k of keys.slice(0, keys.length - MAX_FLAGS)) {
      if (k !== keep) localStorage.removeItem(k);
    }
  } catch {
    /* private mode */
  }
}

/** Confetti: ~150 particles, gravity + drag + spin. Ported from app.js. */
function runConfetti(canvas: HTMLCanvasElement, durationMs: number): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const resize = (): void => {
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;
  };
  resize();
  window.addEventListener('resize', resize);

  // Gold-dominant palette with green accents — the approved 11 entries.
  const colors = [
    '#ffe89a', '#ffd674', '#f5b94a', '#ffe89a', '#ffd674',
    '#f5b94a', '#c08c1e', '#ffba3d',
    '#2dd05f', '#5eea83',
    '#ffffff',
  ];
  type P = { x: number; y: number; vx: number; vy: number; w: number; h: number; color: string; rot: number; spin: number };
  const make = (offset?: number): P => ({
    x: Math.random() * canvas.width,
    y: -20 - Math.random() * 80 * (offset ?? 1),
    vx: (Math.random() - 0.5) * 6 * dpr,
    vy: Math.random() * 2 * dpr,
    w: (6 + Math.random() * 6) * dpr,
    h: (10 + Math.random() * 8) * dpr,
    color: colors[(Math.random() * colors.length) | 0]!,
    rot: Math.random() * Math.PI * 2,
    spin: (Math.random() - 0.5) * 0.22,
  });

  const N = 150;
  const particles: P[] = [];
  for (let i = 0; i < N; i++) particles.push(make(i / N));

  let raf = 0;
  let running = true;
  const startedAt = performance.now();

  const loop = (now: number): void => {
    if (!running) return;
    const elapsed = now - startedAt;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const p of particles) {
      p.vy += 0.16 * dpr; // gravity
      p.vx *= 0.992;      // drag
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.spin;
      // Recycle for the first stretch so the burst feels sustained.
      if (p.y > canvas.height + 24 && elapsed < durationMs - 800) {
        Object.assign(p, make());
        p.y = -20;
      }
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = Math.max(0, 1 - Math.max(0, elapsed - durationMs + 800) / 800);
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    }
    if (elapsed < durationMs) raf = requestAnimationFrame(loop);
    else {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      running = false;
    }
  };
  raf = requestAnimationFrame(loop);

  return () => {
    running = false;
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', resize);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
}

export function StopWinCelebration() {
  const { t } = useT();
  // Same query key the bot screens already poll — shared cache, no extra load.
  const state = useBotState();
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [countdown, setCountdown] = useState(AUTOCLOSE_SECONDS);
  const [paused, setPaused] = useState(false);
  const [pnlText, setPnlText] = useState('+$0.00');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const close = useCallback(() => setSnap(null), []);

  // ── trigger ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#celebrate-test') {
      setSnap(PREVIEW);
      return;
    }
    const s: BotState | undefined = state.data;
    if (!s || !s.halted || !isStopWinHalt(s.haltReason)) return;
    // CELEBRATE EVERY STOP-WIN, NOT ONCE A DAY (client, 2026-09-23):
    // "if I reset the counters or start another session and the bot reaches
    // the Stop Win again, the animation should display again."
    //
    // The old flag was keyed on dayKey, so a second target in the same day was
    // silent. The flag still has to exist, because this effect runs on every
    // poll of the bot state (2 s) and `halted` stays true for the whole halt —
    // without it the overlay would re-open the instant the customer closed it.
    //
    // So the key is now the HALT, not the day: the totals at the moment of the
    // halt. Resetting the counters or hitting the target again in a new session
    // produces different totals, hence a new key, hence a new celebration —
    // while the same continuing halt keeps the same key and stays dismissed.
    const dayKey = s.daily?.dayKey || new Date().toISOString().slice(0, 10);
    const haltId = [
      dayKey,
      s.daily?.cyclesCompleted ?? 0,
      s.daily?.totalTrades ?? 0,
      Number(s.daily?.netProfitUsd ?? 0).toFixed(2),
    ].join('_');
    const flag = FLAG_PREFIX + haltId;
    if (readFlag(flag)) return;
    writeFlag(flag);
    pruneFlags(flag);
    setSnap({
      netProfitUsd: Number(s.daily.netProfitUsd || 0),
      // cyclesCompleted only — the approved overlay matched the dashboard's
      // Cycles tile; adding abandoned cycles made the two disagree.
      cycles: s.daily.cyclesCompleted || 0,
      wins: Number(s.daily.cycleWins || 0),
      losses: Number(s.daily.cycleLosses || 0),
    });
  }, [state.data]);

  // ── money count-up (ease-out cubic, 1.5 s) ────────────────────────────────
  useEffect(() => {
    if (!snap) return;
    const target = snap.netProfitUsd;
    const start = performance.now();
    let raf = 0;
    const frame = (now: number): void => {
      const p = Math.min(1, (now - start) / 1500);
      const eased = 1 - Math.pow(1 - p, 3);
      setPnlText(`+${money(target * eased)}`);
      if (p < 1) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [snap]);

  // ── confetti + scroll lock ────────────────────────────────────────────────
  useEffect(() => {
    if (!snap) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const stop = canvasRef.current ? runConfetti(canvasRef.current, 5000) : () => {};
    return () => {
      document.body.style.overflow = prev;
      stop();
    };
  }, [snap]);

  // ── auto-close, paused while the customer is reading it ───────────────────
  useEffect(() => {
    if (!snap) { setCountdown(AUTOCLOSE_SECONDS); return; }
    if (paused) return;
    if (countdown <= 0) { close(); return; }
    const id = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(id);
  }, [snap, paused, countdown, close]);

  // Escape closes it, like any dialog.
  useEffect(() => {
    if (!snap) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [snap, close]);

  if (!snap) return null;

  const decided = snap.wins + snap.losses;
  const winRate = decided > 0 ? `${Math.round((snap.wins / decided) * 100)}%` : '—';

  return (
    <div className="celebration">
      <canvas className="celebration-confetti" ref={canvasRef} />
      <div className="celebration-backdrop" onClick={close} />
      <div className="celebration-glow" />
      <div
        className="celebration-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="celebration-title"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <button className="celebration-x" onClick={close} aria-label={t('common.close')} type="button">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="6" y1="6" x2="18" y2="18" />
            <line x1="18" y1="6" x2="6" y2="18" />
          </svg>
        </button>

        <div className="celebration-mascot-wrap" aria-hidden="true">
          <img className="celebration-mascot" src="/logo.png" alt="$mallX" />
        </div>

        {/* Gold trophy with $ medallion and a green energy ring at the base. */}
        <div className="celebration-trophy" aria-hidden="true">
          <svg viewBox="0 0 200 220" width="140" height="160">
            <defs>
              <linearGradient id="gold3d" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#fff2b3" />
                <stop offset="20%" stopColor="#ffd674" />
                <stop offset="55%" stopColor="#f5b94a" />
                <stop offset="80%" stopColor="#c08c1e" />
                <stop offset="100%" stopColor="#7a4d10" />
              </linearGradient>
              <linearGradient id="goldSide" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#a47214" />
                <stop offset="50%" stopColor="#f5b94a" />
                <stop offset="100%" stopColor="#a47214" />
              </linearGradient>
              <radialGradient id="trophyShine" cx="0.3" cy="0.25" r="0.5">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
                <stop offset="50%" stopColor="#ffffff" stopOpacity="0.15" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="medal" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#ffe89a" />
                <stop offset="70%" stopColor="#d8961f" />
                <stop offset="100%" stopColor="#7a4d10" />
              </radialGradient>
              <radialGradient id="baseGlow" cx="0.5" cy="0.5" r="0.5">
                <stop offset="0%" stopColor="#2dd05f" stopOpacity="0.8" />
                <stop offset="60%" stopColor="#2dd05f" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#2dd05f" stopOpacity="0" />
              </radialGradient>
            </defs>

            <ellipse cx="100" cy="200" rx="80" ry="14" fill="url(#baseGlow)" />
            <ellipse cx="100" cy="200" rx="70" ry="6" fill="none" stroke="#5eea83" strokeWidth="1.5" opacity="0.7" />
            <ellipse cx="100" cy="200" rx="55" ry="4" fill="none" stroke="#2dd05f" strokeWidth="1" opacity="0.5" />

            <rect x="68" y="184" width="64" height="12" rx="2" fill="url(#goldSide)" />
            <rect x="76" y="172" width="48" height="14" rx="2" fill="url(#gold3d)" stroke="#7a4d10" strokeWidth="0.5" />
            <rect x="90" y="148" width="20" height="26" fill="url(#goldSide)" />

            <path d="M 50 50 Q 50 130 100 145 Q 150 130 150 50 Z" fill="url(#gold3d)" stroke="#7a4d10" strokeWidth="1" />
            <path d="M 50 60 Q 22 65 22 90 Q 22 110 45 110" fill="none" stroke="url(#gold3d)" strokeWidth="8" strokeLinecap="round" />
            <path d="M 150 60 Q 178 65 178 90 Q 178 110 155 110" fill="none" stroke="url(#gold3d)" strokeWidth="8" strokeLinecap="round" />

            <rect x="48" y="44" width="104" height="10" rx="2" fill="url(#gold3d)" stroke="#7a4d10" strokeWidth="0.5" />

            <circle cx="100" cy="92" r="28" fill="url(#medal)" stroke="#7a4d10" strokeWidth="1.5" />
            <text x="100" y="103" fontSize="38" fontWeight="900" fontFamily="Inter, system-ui, sans-serif" textAnchor="middle" fill="#7a4d10">$</text>

            <ellipse cx="80" cy="70" rx="22" ry="32" fill="url(#trophyShine)" />

            <g className="trophy-sparkles">
              <circle cx="160" cy="40" r="2" fill="#fff" opacity="0.9" />
              <circle cx="40" cy="30" r="1.5" fill="#fff" opacity="0.85" />
              <circle cx="170" cy="95" r="1.5" fill="#fff" opacity="0.8" />
              <circle cx="30" cy="100" r="2" fill="#fff" opacity="0.9" />
              <circle cx="100" cy="20" r="2.5" fill="#fff" opacity="1" />
            </g>
          </svg>
        </div>

        <h2 className="celebration-title" id="celebration-title">
          {t('cel.titleBefore')} <span className="cel-accent">{t('cel.titleAccent')}</span> {t('cel.titleAfter')}
        </h2>
        <p className="celebration-sub">
          <span className="cel-accent-tx">{t('cel.stopWin')}</span> {t('cel.subtitle')}
        </p>

        <div className="celebration-pnl-label">{t('cel.todaysProfit')}</div>
        <div className="celebration-pnl">{pnlText}</div>

        <div className="celebration-meta">
          <div className="celebration-meta-cell">
            <span className="cel-stat-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#5eea83" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </span>
            <div className="celebration-meta-val">{snap.cycles}</div>
            <div className="celebration-meta-lbl">{t('dash.cycles')}</div>
          </div>
          <div className="celebration-meta-cell">
            <span className="cel-stat-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#5eea83" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 17 9 11 13 15 21 7" />
                <polyline points="14 7 21 7 21 14" />
              </svg>
            </span>
            <div className="celebration-meta-val">{winRate}</div>
            <div className="celebration-meta-lbl">{t('dash.winRate')}</div>
          </div>
          <div className="celebration-meta-cell">
            <span className="cel-stat-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#ed3838" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <circle cx="12" cy="12" r="5" />
                <circle cx="12" cy="12" r="1.5" fill="#ed3838" />
                <path d="M19 5l-3 3" />
              </svg>
            </span>
            <div className="celebration-meta-val">
              <span className="win-num">{snap.wins}</span>
              <span className="hs-sep">·</span>
              <span className="loss-num">{snap.losses}</span>
            </div>
            <div className="celebration-meta-lbl">{t('dash.cycleWL')}</div>
          </div>
        </div>

        <div className="celebration-waveform" aria-hidden="true">
          <svg className="cel-wave cel-wave-left" viewBox="0 0 100 30" preserveAspectRatio="none">
            <path d="M 0 15 Q 8 5 16 15 T 32 15 T 48 15 T 64 15 T 80 15 T 100 15" stroke="#2dd05f" strokeWidth="1.5" fill="none" />
          </svg>
          <span className="cel-check">
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="#2dd05f" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="5 12 10 17 19 8" />
            </svg>
          </span>
          <svg className="cel-wave cel-wave-right" viewBox="0 0 100 30" preserveAspectRatio="none">
            <path d="M 0 15 Q 8 5 16 15 T 32 15 T 48 15 T 64 15 T 80 15 T 100 15" stroke="#2dd05f" strokeWidth="1.5" fill="none" />
          </svg>
        </div>

        <button className="celebration-close" onClick={close} type="button">
          <span className="cel-btn-emoji">🎉</span>
          <span className="cel-btn-text">{t('cel.cta')}</span>
          <span className="cel-btn-arrow">›</span>
        </button>

        <div className="celebration-autoclose">
          {t('cel.autoclose')} <span className="cel-count">{countdown}</span> {t('cel.seconds')}
        </div>
      </div>
    </div>
  );
}
